import { Fragment, useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Users, Search, Filter, CheckCircle, ChevronDown, XCircle, Loader2, RefreshCw, Eye, Ban, RotateCcw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ParticipanteMecanica } from "@/lib/fakeApi";
import { queueApiService } from "@/services/queueApi.service";
import type { CustomFieldConfig } from "@/components/queue/CustomFieldsBuilder";
import CustomFieldsDetail, { formatCustomFieldInline } from "@/components/queue/CustomFieldsDetail";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";

export default function EventsParticipantes() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [participantes, setParticipantes] = useState<ParticipanteMecanica[]>([]);
  const [filteredParticipantes, setFilteredParticipantes] = useState<ParticipanteMecanica[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [rowDropdownOpenId, setRowDropdownOpenId] = useState<string | null>(null);
  const { toast } = useToast();
  const [mecanicaStatus, setMecanicaStatus] = useState<'inactive' | 'active' | 'paused' | 'completed' | 'cancelled'>('inactive');
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);
  const [isTogglingMecanica, setIsTogglingMecanica] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [sendingMessageId, setSendingMessageId] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [queueStartTime, setQueueStartTime] = useState<string | null>(null);
  const [estimatedServiceTime, setEstimatedServiceTime] = useState<number>(3); // Tempo médio padrão em minutos
  const [customFieldsConfig, setCustomFieldsConfig] = useState<CustomFieldConfig[]>([]);
  const [selectedParticipant, setSelectedParticipant] = useState<ParticipanteMecanica | null>(null);
  const [queueName, setQueueName] = useState<string>('');
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [activeEntriesCount, setActiveEntriesCount] = useState(0);
  
  const handleRefresh = async () => {
    if (id && !isRefreshing) {
      setIsRefreshing(true);
      try {
        await loadParticipantes(id, debouncedSearchTerm, statusFilter, currentPage, itemsPerPage);
        toast({
          title: "Lista atualizada",
          description: "A lista de participantes foi atualizada com sucesso",
        });
      } catch (error) {
        toast({
          title: "Erro ao atualizar",
          description: "Não foi possível atualizar a lista",
          variant: "destructive",
        });
      } finally {
        setIsRefreshing(false);
      }
    }
  };
  
  useEffect(() => {
    if (id) {
      // Only load queue status, not participants (they will be loaded by the other useEffect)
      queueApiService.getQueue(id).then(m => {
        // Salvar nome da fila
        if (m?.nome) {
          setQueueName(m.nome);
        }
        // Usa o status real do backend se disponível
        if (m?.realStatus) {
          setMecanicaStatus(m.realStatus);
        } else {
          // Fallback para mapeamento antigo se necessário
          setMecanicaStatus('inactive');
        }
        
        // Armazenar o horário de início agendado se existir
        if (m?.serviceStartTime) {
          setQueueStartTime(typeof m.serviceStartTime === 'string' ? m.serviceStartTime : String(m.serviceStartTime));
        }

        // Armazenar o tempo médio de atendimento
        if (m?.estimatedServiceTime !== undefined && m?.estimatedServiceTime !== null) {
          setEstimatedServiceTime(m.estimatedServiceTime);
        } else {
          // Usar padrão de 3 minutos se não encontrar
          setEstimatedServiceTime(3);
        }
        // Load custom fields config from event level
        if (m?.eventId) {
          queueApiService.getEventCustomFields(m.eventId).then(fields => {
            setCustomFieldsConfig(fields || []);
          }).catch(error => {
            console.error('Error loading custom fields config:', error);
          });
        }
      }).catch(error => {
        console.error('Error loading queue status:', error);
        setMecanicaStatus('inactive');
      });
    }
  }, [id]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page when searching
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    // Only apply filters after debounced search term or status filter changes
    // Not when participantes change (to avoid loops)
    // Also skip if we're updating to avoid conflicts
    if (id && !isUpdating) {
      loadParticipantes(id, debouncedSearchTerm, statusFilter, currentPage, itemsPerPage);
    }
  }, [id, debouncedSearchTerm, statusFilter, currentPage, itemsPerPage, isUpdating]);

  // Fechar dropdowns ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isStatusFilterOpen && !target.closest('.status-dropdown-container')) {
        setIsStatusFilterOpen(false);
      }
      if (rowDropdownOpenId && !target.closest('[data-row-dropdown="true"]')) {
        setRowDropdownOpenId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isStatusFilterOpen, rowDropdownOpenId]);

  const loadParticipantes = async (mecanicaId: string, searchTerm?: string, status?: string, page?: number, limit?: number) => {
    try {
      setIsLoading(true);
      
      // Track when loading started for minimum duration
      const startTime = Date.now();
      
      // Pass filters to API
      const params: any = {
        page: page || currentPage,
        limit: limit || itemsPerPage
      };
      
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      
      if (status && status !== 'todos') {
        // Map frontend status to backend status
        const statusMap: Record<string, string> = {
          'na_fila': 'waiting',
          'concluido': 'completed',
          'desistente': 'abandoned',
          'cancelado': 'cancelled',
          'ausente': 'abandoned'
        };
        params.status = statusMap[status] || status;
      }
      
      const response = await queueApiService.listParticipantsWithPagination(mecanicaId, params);
      
      // Calculate time elapsed and wait for minimum duration (300ms)
      const elapsed = Date.now() - startTime;
      const minimumLoadingTime = 300;
      
      if (elapsed < minimumLoadingTime) {
        await new Promise(resolve => setTimeout(resolve, minimumLoadingTime - elapsed));
      }
      
      setParticipantes(response.entries || []);
      setFilteredParticipantes(response.entries || []);
      setTotalItems(response.total || 0);
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível carregar os participantes",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Remove applyFilters as it's no longer needed - we call loadParticipantes directly

  const handleStatusChange = async (participanteId: string, newStatus: 'na_fila' | 'concluido' | 'desistente' | 'ausente' | 'cancelado') => {
    try {
      setUpdatingStatusId(participanteId);
      setIsUpdating(true);
      
      // Find the current participant to get their current status
      const participant = participantes.find(p => p.id === participanteId);
      const currentStatus = participant?.status;
      
      await queueApiService.updateParticipantStatus(participanteId, newStatus, currentStatus);
      
      // Reload participants with current filters to get updated data from backend
      if (id) {
        await loadParticipantes(id, debouncedSearchTerm, statusFilter);
      }

      toast({
        title: "Sucesso",
        description: "Status do participante atualizado",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível atualizar o status",
        variant: "destructive",
      });
    } finally {
      setUpdatingStatusId(null);
      setIsUpdating(false);
    }
  };


  const handleMarkAsCompleted = async (participanteId: string) => {
    try {
      await handleStatusChange(participanteId, 'concluido');
      toast({
        title: "Sucesso",
        description: "Participante marcado como concluído",
        variant: "default",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível marcar como concluído",
        variant: "destructive",
      });
    }
  };

  const getStatusColor = (status: 'na_fila' | 'concluido' | 'desistente' | 'ausente' | 'cancelado') => {
    switch (status) {
      case 'na_fila':
        return 'border-primary text-primary';
      case 'concluido':
        return 'border-brand-green text-status-ok';
      case 'desistente':
        return 'border-brand-attention text-status-attention';
      case 'ausente':
        return 'border-border text-muted-foreground';
      case 'cancelado':
        return 'border-brand-danger text-status-danger';
      default:
        return 'border-border text-muted-foreground';
    }
  };

  const getStatusLabel = (status: 'na_fila' | 'concluido' | 'desistente' | 'ausente' | 'cancelado') => {
    switch (status) {
      case 'na_fila':
        return 'Na Fila';
      case 'concluido':
        return 'Concluído';
      case 'desistente':
        return 'Desistente';
      case 'ausente':
        return 'Ausente';
      case 'cancelado':
        return 'Cancelado';
      default:
        return status;
    }
  };

  const handleToggleMecanica = async () => {
    try {
      setIsTogglingMecanica(true);
      
      let newStatus: 'active' | 'paused';
      let toastTitle: string;
      let toastDescription: string;
      
      if (mecanicaStatus === 'inactive') {
        // Inactive -> Active (primeira ativação)
        newStatus = 'active';
        toastTitle = 'Mecânica iniciada';
        toastDescription = 'A mecânica foi iniciada e está ativa.';
      } else if (mecanicaStatus === 'active') {
        // Active -> Paused
        newStatus = 'paused';
        toastTitle = 'Mecânica pausada';
        toastDescription = 'A mecânica foi pausada temporariamente.';
      } else if (mecanicaStatus === 'paused') {
        // Paused -> Active
        newStatus = 'active';
        toastTitle = 'Mecânica retomada';
        toastDescription = 'A mecânica está ativa novamente.';
      } else {
        // Completed ou Cancelled - não permitir mudanças
        toast({
          title: 'Ação não permitida',
          description: mecanicaStatus === 'cancelled'
            ? 'Uma mecânica cancelada não pode ser reativada.'
            : 'Uma mecânica finalizada não pode ser reativada.',
          variant: 'destructive'
        });
        return;
      }
      
      // Usar o serviço de API que já tem o contexto correto
      await queueApiService.updateQueueStatus(id as string, newStatus);
      
      setMecanicaStatus(newStatus);
      toast({ title: toastTitle, description: toastDescription });
    } catch (e: any) {
      console.error('Error updating status:', e);
      toast({ 
        title: 'Erro', 
        description: e.message || 'Não foi possível atualizar o status', 
        variant: 'destructive' 
      });
    } finally {
      setIsTogglingMecanica(false);
    }
  };
  
  const handleCompleteMecanica = async () => {
    try {
      setIsTogglingMecanica(true);
      
      // Usar o serviço de API para finalizar
      await queueApiService.updateQueueStatus(id as string, 'completed');
      
      setMecanicaStatus('completed');
      setShowCompleteDialog(false);
      toast({ 
        title: 'Mecânica finalizada', 
        description: 'A mecânica foi finalizada com sucesso.' 
      });
    } catch (e: any) {
      console.error('Error completing queue:', e);
      toast({ 
        title: 'Erro', 
        description: e.message || 'Não foi possível finalizar a mecânica', 
        variant: 'destructive' 
      });
    } finally {
      setIsTogglingMecanica(false);
    }
  };

  const handleCancelMecanica = async () => {
    if (!cancelReason.trim()) return;

    try {
      setIsTogglingMecanica(true);

      await queueApiService.cancelQueue(id as string, cancelReason.trim());

      setMecanicaStatus('cancelled');
      setShowCancelDialog(false);
      setCancelReason('');
      toast({
        title: 'Mecânica cancelada',
        description: 'A mecânica foi cancelada e todos os participantes foram notificados.'
      });

      // Reload participants to reflect the status changes
      if (id) {
        await loadParticipantes(id, debouncedSearchTerm, statusFilter, currentPage, itemsPerPage);
      }
    } catch (e: any) {
      console.error('Error cancelling queue:', e);
      toast({
        title: 'Erro',
        description: e?.response?.data?.message || e.message || 'Não foi possível cancelar a mecânica',
        variant: 'destructive'
      });
    } finally {
      setIsTogglingMecanica(false);
    }
  };

  const handleResetQueue = async () => {
    if (!id) return;
    try {
      const { count } = await queueApiService.getActiveEntriesCount(id);
      setActiveEntriesCount(count);
      setShowResetDialog(true);
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível obter a contagem de participantes ativos',
        variant: 'destructive',
      });
    }
  };

  const confirmResetQueue = async () => {
    if (!id) return;
    try {
      setIsResetting(true);
      await queueApiService.resetQueue(id);

      setMecanicaStatus('paused');
      setShowResetDialog(false);

      // Reload participants — reset entries are hidden by backend (resetAt flag)
      setCurrentPage(1);
      await loadParticipantes(id, debouncedSearchTerm, statusFilter, 1, itemsPerPage);

      toast({
        title: 'Fila resetada',
        description: 'A fila foi resetada com sucesso. Todos os participantes ativos foram marcados como ausentes.',
      });
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error?.response?.data?.message || 'Não foi possível resetar a fila',
        variant: 'destructive',
      });
    } finally {
      setIsResetting(false);
    }
  };

  // Custom fields visible in operator list, sorted by order
  const operatorVisibleFields = customFieldsConfig
    .filter(f => f.showInOperatorList)
    .sort((a, b) => a.order - b.order);



  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-brand-logo-1"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/mecanicas?${searchParams.toString()}`)}
            className="p-2 hover:bg-muted rounded-xl transition-colors"
          >
            <ArrowLeft className="text-foreground" size={24} />
          </button>
          <div>
            <h1 className="text-[16px] font-bold text-foreground">
              Lista de Participantes
            </h1>
            <p className="text-[13px] text-muted-foreground">
              {queueName ? `Fila: ${queueName}` : 'Gerencie os participantes da mecânica'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {mecanicaStatus !== 'completed' && mecanicaStatus !== 'cancelled' && (
            <div className={`inline-flex items-center gap-3 px-3 py-2 rounded-xl border ${
              mecanicaStatus === 'inactive' ? 'bg-muted border-border/30' :
              mecanicaStatus === 'active' ? 'bg-brand-green/10 border-brand-green/30' :
              'bg-brand-attention/10 border-brand-attention/30'
            } shadow-sm`}>
              <span className={`text-sm font-medium ${
                mecanicaStatus === 'inactive' ? 'text-muted-foreground' :
                mecanicaStatus === 'active' ? 'text-status-ok' :
                'text-status-attention'
              }`}>
                {mecanicaStatus === 'inactive' ? 'Não Iniciada' :
                 mecanicaStatus === 'active' ? 'Ativa' :
                 'Pausada'}
              </span>
              <button
                onClick={handleToggleMecanica}
                disabled={isTogglingMecanica}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors duration-200 ease-in-out ${
                  mecanicaStatus === 'active' ? 'bg-brand-green' : 
                  mecanicaStatus === 'paused' ? 'bg-brand-attention' : 
                  'bg-muted'
                } border-2 border-transparent ${isTogglingMecanica ? 'opacity-50 cursor-not-allowed' : ''}`}
                aria-label="Alternar status da mecânica"
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-card shadow ring-0 transition duration-200 ease-in-out ${
                    mecanicaStatus === 'active' ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          )}
          
          {mecanicaStatus === 'completed' && (
            <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-muted border border-border/30 shadow-sm">
              <CheckCircle className="text-muted-foreground" size={16} />
              <span className="text-sm font-medium text-muted-foreground">Finalizada</span>
            </div>
          )}

          {mecanicaStatus === 'cancelled' && (
            <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-brand-danger/10 border border-brand-danger/30 shadow-sm">
              <Ban className="text-status-danger" size={16} />
              <span className="text-sm font-medium text-status-danger">Cancelada</span>
            </div>
          )}
          
          {(mecanicaStatus === 'active' || mecanicaStatus === 'paused') && (
            <>
              <button
                onClick={handleResetQueue}
                disabled={isTogglingMecanica || isResetting}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-muted border border-border text-muted-foreground hover:bg-brand-logo-1/10 hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Resetar fila para novo dia"
              >
                <RotateCcw size={16} />
                <span className="text-sm font-medium">Resetar</span>
              </button>
              <button
                onClick={() => setShowCancelDialog(true)}
                disabled={isTogglingMecanica}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-danger/10 border border-brand-danger/30 text-status-danger hover:bg-brand-danger/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Ban size={16} />
                <span className="text-sm font-medium">Cancelar Mecânica</span>
              </button>
              <button
                onClick={() => setShowCompleteDialog(true)}
                disabled={isTogglingMecanica}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-danger/10 border border-brand-danger/30 text-status-danger hover:bg-brand-danger/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <XCircle size={16} />
                <span className="text-sm font-medium">Finalizar Mecânica</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Users className="text-primary" size={20} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total</p>
              <p className="text-2xl font-bold text-foreground">{participantes.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-brand-green/10 rounded-full flex items-center justify-center">
              <Users className="text-status-ok" size={20} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Na Fila</p>
              <p className="text-2xl font-bold text-foreground">
                {participantes.filter(p => p.status === 'na_fila').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Users className="text-primary" size={20} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Concluídos</p>
              <p className="text-2xl font-bold text-foreground">
                {participantes.filter(p => p.status === 'concluido').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-brand-attention/10 rounded-full flex items-center justify-center">
              <Users className="text-status-attention" size={20} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Outros</p>
              <p className="text-2xl font-bold text-foreground">
                {participantes.filter(p => !['na_fila', 'concluido'].includes(p.status)).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
              <input
                type="text"
                placeholder="Buscar por nome ou telefone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-full border border-input bg-card py-2 pl-10 pr-4 text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="text-muted-foreground" size={20} />
              <div className="relative status-dropdown-container">
                <button
                  type="button"
                  onClick={() => setIsStatusFilterOpen(!isStatusFilterOpen)}
                  className="px-4 py-2 bg-muted border border-border rounded-[20px] text-foreground focus:outline-none focus:ring-2 focus:ring-brand-logo-1 focus:border-transparent flex items-center justify-between min-w-[180px]"
                >
                  <span>
                    {statusFilter === 'todos' ? 'Todos os Status' :
                      statusFilter === 'na_fila' ? 'Na Fila' :
                      statusFilter === 'concluido' ? 'Concluído' :
                      statusFilter === 'desistente' ? 'Desistente' :
                      statusFilter === 'ausente' ? 'Ausente' : 'Cancelado'}
                  </span>
                  <ChevronDown className={`ml-2 transition-transform duration-200 ${isStatusFilterOpen ? 'rotate-180' : ''}`} size={16} />
                </button>

                {isStatusFilterOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-muted border border-border rounded-xl shadow-lg z-50 overflow-hidden">
                    {[
                      { value: 'todos', label: 'Todos os Status' },
                      { value: 'na_fila', label: 'Na Fila' },
                      { value: 'concluido', label: 'Concluído' },
                      { value: 'desistente', label: 'Desistente' },
                      { value: 'ausente', label: 'Ausente' },
                      { value: 'cancelado', label: 'Cancelado' },
                    ].map(option => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => { 
                          setStatusFilter(option.value); 
                          setIsStatusFilterOpen(false);
                          setCurrentPage(1); // Reset to first page when changing filter
                        }}
                        className="w-full px-4 py-3 text-left text-foreground hover:bg-brand-logo-1/10 transition-colors first:rounded-t-xl last:rounded-b-xl"
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing || isLoading}
              className="p-2 bg-muted border border-border rounded-lg hover:bg-brand-logo-1/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Atualizar lista"
            >
              <RefreshCw className={`text-muted-foreground ${isRefreshing ? 'animate-spin' : ''}`} size={20} />
            </button>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Filter size={16} />
            <span>{filteredParticipantes.length} de {participantes.length} participantes</span>
          </div>
        </div>
      </div>

      {/* Participants Table */}
      <div className="bg-card border border-border rounded-xl" style={{ position: 'relative', zIndex: 1 }}>
        {filteredParticipantes.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Users className="mx-auto mb-4 text-muted-foreground" size={48} />
            <h4 className="font-medium text-foreground mb-2">Nenhum participante encontrado</h4>
            <p className="text-sm">Tente ajustar os filtros de busca</p>
          </div>
        ) : (
          <>
            <div className="relative" style={{ overflowX: 'clip' }}>
              <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left px-4 pt-4 pb-3 text-[12px] font-semibold text-[var(--text-faint-2)]">Nome</th>
                  <th className="text-left px-4 pt-4 pb-3 text-[12px] font-semibold text-[var(--text-faint-2)]">Telefone</th>
                  <th className="text-left px-4 pt-4 pb-3 text-[12px] font-semibold text-[var(--text-faint-2)]">Posição</th>
                  <th className="text-left px-4 pt-4 pb-3 text-[12px] font-semibold text-[var(--text-faint-2)]">Estimativa</th>
                  {operatorVisibleFields.map(field => (
                    <th key={field.fieldKey} className="text-left whitespace-nowrap px-4 pt-4 pb-3 text-[12px] font-semibold text-[var(--text-faint-2)]">
                      {field.label || field.fieldKey}
                    </th>
                  ))}
                  <th className="text-left px-4 pt-4 pb-3 text-[12px] font-semibold text-[var(--text-faint-2)]">Status</th>
                  <th className="text-right px-4 pt-4 pb-3 text-[12px] font-semibold text-[var(--text-faint-2)]">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredParticipantes.map((participante, index) => {
                  // Usar a posição real do participante (vinda do backend)
                  const posicaoReal = participante.posicao;
                  
                  // Função para converter estimativa em minutos para hora HH:mm
                  const converterEstimativaParaHora = (estimativaStr: string) => {
                    // Extrai o número de minutos da string "X min"
                    const match = estimativaStr.match(/(\d+)\s*min/);
                    if (!match) return undefined;
                    
                    const minutosEstimados = parseInt(match[1]);
                    let horaBase: Date;
                    
                    // Se a fila tem horário agendado e ainda não começou, usar esse horário como base
                    if (queueStartTime) {
                      const startTime = new Date(queueStartTime);
                      const now = new Date();
                      
                      // Se o horário agendado é futuro, usar ele como base
                      if (startTime > now) {
                        horaBase = new Date(startTime);
                      } else {
                        // Se já passou do horário agendado, usar hora atual
                        horaBase = new Date();
                      }
                    } else {
                      // Sem horário agendado, usar hora atual
                      horaBase = new Date();
                    }
                    
                    horaBase.setMinutes(horaBase.getMinutes() + minutosEstimados);
                    
                    // Formatar hora no formato HH:mm
                    const horas = horaBase.getHours().toString().padStart(2, '0');
                    const minutos = horaBase.getMinutes().toString().padStart(2, '0');
                    
                    return `${horas}:${minutos}`;
                  };
                  
                  // Calcular hora estimada de atendimento baseada na posição
                  const calcularHoraEstimada = () => {
                    if (!posicaoReal || participante.status !== 'na_fila') return undefined;
                    
                    // Usar o tempo médio de atendimento da fila
                    const minutosEstimados = posicaoReal * estimatedServiceTime;
                    let horaBase: Date;
                    
                    // Se a fila tem horário agendado e ainda não começou, usar esse horário como base
                    if (queueStartTime) {
                      const startTime = new Date(queueStartTime);
                      const now = new Date();
                      
                      // Se o horário agendado é futuro, usar ele como base
                      if (startTime > now) {
                        horaBase = new Date(startTime);
                      } else {
                        // Se já passou do horário agendado, usar hora atual
                        horaBase = new Date();
                      }
                    } else {
                      // Sem horário agendado, usar hora atual
                      horaBase = new Date();
                    }
                    
                    horaBase.setMinutes(horaBase.getMinutes() + minutosEstimados);
                    
                    // Formatar hora no formato HH:mm
                    const horas = horaBase.getHours().toString().padStart(2, '0');
                    const minutos = horaBase.getMinutes().toString().padStart(2, '0');
                    
                    return `${horas}:${minutos}`;
                  };
                  
                  // Se tem estimativa do backend, converter para hora; senão calcular baseado na posição
                  const mostrarEstimativa = participante.estimativa 
                    ? converterEstimativaParaHora(participante.estimativa) 
                    : calcularHoraEstimada();
                  
                  // Determinar se o dropdown deve abrir para cima (últimas 3 linhas)
                  const shouldOpenUpward = index >= filteredParticipantes.length - 3;

                  return (
                    <Fragment key={participante.id}>
                    <tr className="border-t border-border hover:bg-[var(--tabela-linha-hover)] transition-colors">
                      <td className="px-4 py-4 align-middle text-[13px]">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-brand-logo-1/10 rounded-full flex items-center justify-center">
                            <span className="text-brand-logo-1 text-sm font-medium">
                              {participante.nome.split(' ').map(n => n[0]).join('').substring(0, 2)}
                            </span>
                          </div>
                          <span className="font-medium text-foreground">{participante.nome}</span>
                          {customFieldsConfig.length > 0 && (
                            <button
                              onClick={() => setSelectedParticipant(participante)}
                              className="p-1 hover:bg-brand-logo-1/10 rounded-lg transition-colors group"
                              title="Ver campos personalizados"
                            >
                              <Eye size={14} className="text-muted-foreground group-hover:text-brand-logo-1" />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 align-middle text-[13px] text-foreground">{participante.telefone}</td>
                      <td className="px-4 py-4 align-middle text-[13px] text-foreground">
                        {posicaoReal && participante.status === 'na_fila' ? (
                          <span className="font-medium text-brand-logo-1">#{posicaoReal}</span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 align-middle text-[13px] text-foreground">{mostrarEstimativa ?? <span className="text-muted-foreground">-</span>}</td>
                      {operatorVisibleFields.map(field => (
                        <td key={field.fieldKey} className="px-4 py-4 align-middle text-[13px] text-foreground whitespace-nowrap">
                          {participante.customFields
                            ? formatCustomFieldInline(participante.customFields[field.fieldKey], field)
                            : <span className="text-muted-foreground">-</span>
                          }
                        </td>
                      ))}
                      <td className="px-4 py-4 align-middle text-[13px] relative">
                        <div className="flex items-center gap-2" data-row-dropdown="true">
                          <button
                            type="button"
                            onClick={() => setRowDropdownOpenId(prev => prev === participante.id ? null : participante.id)}
                            className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(participante.status)} bg-transparent outline-none cursor-pointer flex items-center gap-1`}
                          >
                            {getStatusLabel(participante.status)}
                            <ChevronDown size={14} className={`transition-transform ${rowDropdownOpenId === participante.id ? 'rotate-180' : ''}`} />
                          </button>
                          {rowDropdownOpenId === participante.id && (
                            <div
                              className="absolute bg-muted border border-border rounded-xl shadow-lg min-w-[180px]"
                              style={{
                                zIndex: 9999,
                                ...(shouldOpenUpward
                                  ? { bottom: '100%', marginBottom: '4px' }
                                  : { top: '100%', marginTop: '4px' }),
                                left: 0,
                              }}>
                              {[
                                { value: 'na_fila', label: 'Na Fila' },
                                { value: 'concluido', label: 'Concluído' },
                                { value: 'desistente', label: 'Desistente' },
                                { value: 'ausente', label: 'Ausente' },
                                { value: 'cancelado', label: 'Cancelado' },
                              ]
                                // Filter out 'concluido' if participant is already completed (backend blocks this transition)
                                .filter(option => !(participante.status === 'concluido' && option.value === 'concluido'))
                                .map(option => (
                                <button
                                  key={option.value}
                                  type="button"
                                  onClick={() => { handleStatusChange(participante.id, option.value as any); setRowDropdownOpenId(null); }}
                                  disabled={updatingStatusId === participante.id || option.value === participante.status}
                                  className={`w-full px-4 py-3 text-left text-foreground hover:bg-brand-logo-1/10 transition-colors first:rounded-t-xl last:rounded-b-xl ${updatingStatusId === participante.id || option.value === participante.status ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                  {option.label}
                                  {option.value === participante.status && ' (atual)'}
                                </button>
                              ))}
                            </div>
                          )}
                          <button
                            onClick={() => handleMarkAsCompleted(participante.id)}
                            disabled={updatingStatusId === participante.id}
                            className={`p-1 hover:bg-brand-green/10 rounded-lg transition-colors group ${updatingStatusId === participante.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                            title="Marcar como concluído"
                          >
                            <CheckCircle size={16} className={`text-muted-foreground ${updatingStatusId === participante.id ? '' : 'group-hover:text-status-ok'}`} />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-4 align-middle text-[13px]">
                        <div className="flex items-center justify-end">
                          <button
                            onClick={async () => {
                              if (sendingMessageId === participante.id) return; // Prevent multiple clicks
                              
                              setSendingMessageId(participante.id);
                              try {
                                const novo = await queueApiService.incrementParticipantMessage(participante.id);
                                
                                // Update both the main list and filtered list
                                setParticipantes(prev => prev.map(p => 
                                  p.id === participante.id ? { ...p, numeroMensagem: novo } : p
                                ));
                                setFilteredParticipantes(prev => prev.map(p => 
                                  p.id === participante.id ? { ...p, numeroMensagem: novo } : p
                                ));
                                
                                // Show success toast with more details
                                toast({ 
                                  title: "✅ Mensagem enviada com sucesso!",
                                  description: `${participante.nome} recebeu a mensagem Nº ${novo} via WhatsApp${participante.telefone ? ` (${participante.telefone})` : ''}`,
                                  duration: 4000
                                });
                              } catch (error) {
                                console.error('Error sending message:', error);
                                toast({ 
                                  title: "❌ Erro ao enviar mensagem",
                                  description: `Não foi possível enviar a mensagem para ${participante.nome}. Por favor, tente novamente.`,
                                  variant: "destructive",
                                  duration: 5000
                                });
                              } finally {
                                setSendingMessageId(null);
                              }
                            }}
                            className="btn-primary px-4 py-2 text-sm min-w-[200px] flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={
                              participante.status !== 'na_fila' || 
                              (participante.posicao !== undefined && participante.posicao > 10) || 
                              (participante.numeroMensagem !== undefined && participante.numeroMensagem >= 3) || 
                              sendingMessageId === participante.id
                            }
                            title={
                              participante.posicao && participante.posicao > 10
                                ? 'Disponível apenas para as 10 primeiras posições'
                                : participante.numeroMensagem && participante.numeroMensagem >= 3
                                ? 'Limite de 3 envios atingido'
                                : participante.status !== 'na_fila'
                                ? 'Disponível apenas para participantes na fila'
                                : ''
                            }
                          >
                            {sendingMessageId === participante.id ? (
                              <Loader2 className="animate-spin" size={20} />
                            ) : (
                              (participante.numeroMensagem ?? 0) === 0
                                ? 'Enviar mensagem'
                                : `Reenviar mensagem (${participante.numeroMensagem}/3)`
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {!isLoading && filteredParticipantes.length > 0 && (
            <div className="flex items-center justify-between mt-6 px-6 py-4 bg-card border border-border rounded-xl">
              <div className="flex items-center gap-6">
                <span className="text-sm text-muted-foreground">
                  Mostrando <span className="font-medium text-foreground">{((currentPage - 1) * itemsPerPage) + 1}</span> a{' '}
                  <span className="font-medium text-foreground">{Math.min(currentPage * itemsPerPage, totalItems)}</span> de{' '}
                  <span className="font-medium text-foreground">{totalItems}</span> participantes
                </span>
                
                {/* Seletor de itens por página */}
                <div className="flex items-center gap-2">
                  <label className="text-sm text-muted-foreground">Itens por página:</label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-3 py-1.5 bg-background border border-border rounded-lg text-sm text-foreground 
                               focus:outline-none focus:ring-2 focus:ring-brand-logo-1 focus:border-brand-logo-1
                               hover:border-brand-logo-1/50 transition-colors cursor-pointer"
                  >
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>
              </div>
              
              {/* Controles de navegação */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                    currentPage === 1 
                      ? 'bg-muted text-muted-foreground cursor-not-allowed opacity-50' 
                      : 'bg-background border border-border text-foreground hover:bg-muted hover:border-brand-logo-1/30'
                  }`}
                >
                  Anterior
                </button>
                
                <div className="flex items-center gap-1">
                  {/* Páginas */}
                  {Array.from({ length: Math.ceil(totalItems / itemsPerPage) }, (_, i) => i + 1)
                    .filter(page => {
                      const totalPages = Math.ceil(totalItems / itemsPerPage);
                      return page === 1 || 
                             page === totalPages || 
                             Math.abs(page - currentPage) <= 1;
                    })
                    .map((page, index, array) => (
                      <Fragment key={page}>
                        {index > 0 && array[index - 1] !== page - 1 && (
                          <span className="px-2 text-muted-foreground">...</span>
                        )}
                        <button
                          onClick={() => setCurrentPage(page)}
                          className={`min-w-[40px] h-10 px-3 text-sm font-medium rounded-lg transition-all ${
                            page === currentPage
                              ? 'bg-brand-logo-1 text-primary-foreground shadow-sm'
                              : 'bg-background border border-border text-foreground hover:bg-muted hover:border-brand-logo-1/30'
                          }`}
                        >
                          {page}
                        </button>
                      </Fragment>
                    ))}
                </div>
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalItems / itemsPerPage), prev + 1))}
                  disabled={currentPage >= Math.ceil(totalItems / itemsPerPage)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                    currentPage >= Math.ceil(totalItems / itemsPerPage)
                      ? 'bg-muted text-muted-foreground cursor-not-allowed opacity-50'
                      : 'bg-background border border-border text-foreground hover:bg-muted hover:border-brand-logo-1/30'
                  }`}
                >
                  Próximo
                </button>
              </div>
            </div>
          )}
          </>
        )}
      </div>
      
      {/* Modal de Confirmação para Finalizar */}
      <AlertDialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Finalizar Mecânica</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja finalizar esta mecânica? Esta ação não pode ser desfeita.
              Todos os participantes serão notificados e não será possível adicionar novos participantes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCompleteMecanica}
              className="bg-brand-danger hover:bg-brand-danger text-destructive-foreground"
            >
              Finalizar Mecânica
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de Confirmação para Cancelar */}
      <AlertDialog open={showCancelDialog} onOpenChange={(open) => {
        setShowCancelDialog(open);
        if (!open) setCancelReason('');
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar Mecânica</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>
                  Esta ação irá cancelar a mecânica e notificar todos os participantes na fila com a justificativa informada. Esta ação não pode ser desfeita.
                </p>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Justificativa do cancelamento *
                  </label>
                  <textarea
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="Informe o motivo do cancelamento da mecânica..."
                    rows={3}
                    className="w-full px-3 py-2 bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-brand-logo-1 resize-none text-sm"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelMecanica}
              disabled={!cancelReason.trim()}
              className="bg-brand-danger hover:bg-brand-danger text-destructive-foreground disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Confirmar Cancelamento
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de Confirmação para Resetar Fila */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Resetar Fila</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  <strong>{activeEntriesCount}</strong> {activeEntriesCount === 1 ? 'pessoa na fila será marcada como ausente' : 'pessoas na fila serão marcadas como ausentes'}.
                  A fila será pausada e as métricas zeradas.
                </p>
                <p className="text-sm text-muted-foreground">
                  Esta ação não pode ser desfeita. Caso deseje marcar participantes como concluídos, faça isso individualmente antes de resetar.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isResetting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmResetQueue}
              disabled={isResetting}
              className="bg-brand-danger hover:bg-brand-danger text-destructive-foreground disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isResetting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resetando...
                </>
              ) : (
                'Resetar Fila'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Sheet lateral para campos personalizados */}
      <Sheet open={!!selectedParticipant} onOpenChange={(open) => !open && setSelectedParticipant(null)}>
        <SheetContent side="right" className="w-[400px] sm:max-w-[450px] bg-background border-border overflow-y-auto">
          <SheetHeader className="mb-6">
            <SheetTitle className="text-foreground text-xl">
              {selectedParticipant?.nome}
            </SheetTitle>
            <SheetDescription className="text-muted-foreground">
              Detalhes do participante
            </SheetDescription>
          </SheetHeader>

          {selectedParticipant && (
            <div className="space-y-6">
              {/* Informações básicas */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Informações</h4>
                <div className="space-y-3 bg-muted rounded-xl p-4 border border-border">
                  <div>
                    <span className="text-xs text-muted-foreground block">Telefone</span>
                    <span className="text-sm text-foreground font-medium">{selectedParticipant.telefone || '-'}</span>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground block">Posição</span>
                    <span className="text-sm text-foreground font-medium">
                      {selectedParticipant.posicao && selectedParticipant.status === 'na_fila'
                        ? `#${selectedParticipant.posicao}`
                        : '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground block">Status</span>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(selectedParticipant.status)}`}>
                      {getStatusLabel(selectedParticipant.status)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Campos personalizados */}
              {customFieldsConfig.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Campos Personalizados</h4>
                  <div className="bg-muted rounded-xl p-4 border border-border">
                    <CustomFieldsDetail
                      customFields={selectedParticipant.customFields || {}}
                      fieldsConfig={customFieldsConfig}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
